import { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subWeeks,
  subMonths,
  subYears,
  addDays,
  addWeeks,
  addMonths,
  addYears,
  isFuture,
  parseISO,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  eachYearOfInterval,
} from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Expense {
  date: string;
  amount: number;
}

interface TimeFrameOption {
  label: string;
  value: 'day' | 'week' | 'month' | 'year';
}

interface Props {
  expenses: Expense[];
  timeFrame: 'day' | 'week' | 'month' | 'year';
  onTimeFrameChange: (timeFrame: 'day' | 'week' | 'month' | 'year') => void;
  onDateChange: (date: Date) => void;
}

export default function ExpensesBarChart({ expenses, timeFrame, onTimeFrameChange, onDateChange }: Props) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const timeFrameOptions: TimeFrameOption[] = [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
  ];

  const getDateRange = () => {
    switch (timeFrame) {
      case 'day':
        return {
          start: startOfDay(currentDate),
          end: endOfDay(currentDate),
          format: 'HH:mm',
          intervals: eachDayOfInterval,
        };
      case 'week':
        return {
          start: startOfWeek(currentDate, { weekStartsOn: 1 }),
          end: endOfWeek(currentDate, { weekStartsOn: 1 }),
          format: 'EEE',
          intervals: eachDayOfInterval,
        };
      case 'month':
        return {
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate),
          format: 'd MMM',
          intervals: eachDayOfInterval,
        };
      case 'year':
        return {
          start: startOfYear(currentDate),
          end: endOfYear(currentDate),
          format: 'MMM',
          intervals: eachMonthOfInterval,
        };
      default:
        return {
          start: startOfDay(currentDate),
          end: endOfDay(currentDate),
          format: 'HH:mm',
          intervals: eachDayOfInterval,
        };
    }
  };

  const navigate = (direction: 'prev' | 'next') => {
    const modifier = direction === 'prev' ? -1 : 1;
    let newDate: Date;
    switch (timeFrame) {
      case 'day':
        newDate = addDays(currentDate, modifier);
        break;
      case 'week':
        newDate = addWeeks(currentDate, modifier);
        break;
      case 'month':
        newDate = addMonths(currentDate, modifier);
        break;
      case 'year':
        newDate = addYears(currentDate, modifier);
        break;
      default:
        newDate = currentDate;
    }
    setCurrentDate(newDate);
    onDateChange(newDate);
  };

  const canNavigateNext = !isFuture(
    timeFrame === 'day' ? addDays(currentDate, 1) :
    timeFrame === 'week' ? addWeeks(currentDate, 1) :
    timeFrame === 'month' ? addMonths(currentDate, 1) :
    addYears(currentDate, 1)
  );

  const { start, end, format: dateFormat } = getDateRange();

  const filteredExpenses = expenses.filter(expense => {
    const expenseDate = parseISO(expense.date);
    return expenseDate >= start && expenseDate <= end;
  });

  const prepareChartData = () => {
    const dates = getDateRange().intervals({ start, end });
    const labels = dates.map(date => format(date, dateFormat));
    
    const data = dates.map(date => {
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);
      
      return filteredExpenses
        .filter(expense => {
          const expenseDate = parseISO(expense.date);
          return expenseDate >= dayStart && expenseDate <= dayEnd;
        })
        .reduce((sum, expense) => sum + expense.amount, 0);
    });

    return {
      labels,
      datasets: [
        {
          label: 'Total Expenses',
          data,
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgb(75, 192, 192)',
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `€${context.raw.toFixed(2)}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount (€)',
          font: {
            size: 12,
          },
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          maxRotation: 0,
          color: 'rgb(156, 163, 175)',
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 45,
          color: 'rgb(156, 163, 175)',
        },
      },
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10
      }
    },
  };

  // When timeFrame changes, reset to current date
  useEffect(() => {
    const newDate = new Date();
    setCurrentDate(newDate);
    onDateChange(newDate);
  }, [timeFrame]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Expenses Over Time</h2>
        <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
          <select
            value={timeFrame}
            onChange={(e) => onTimeFrameChange(e.target.value as 'day' | 'week' | 'month' | 'year')}
            className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-dark-bg-light border border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-primary-500"
          >
            {timeFrameOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <div className="flex items-center gap-2 ml-auto sm:ml-0">
            <button
              onClick={() => navigate('prev')}
              className="p-2 rounded-lg text-gray-600 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-lighter"
            >
              <i className="fas fa-chevron-left" />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
              {timeFrame === 'day' && format(currentDate, 'MMM d, yyyy')}
              {timeFrame === 'week' && `Week of ${format(getDateRange().start, 'MMM d')}`}
              {timeFrame === 'month' && format(currentDate, 'MMMM yyyy')}
              {timeFrame === 'year' && format(currentDate, 'yyyy')}
            </span>
            <button
              onClick={() => navigate('next')}
              disabled={!canNavigateNext}
              className={`p-2 rounded-lg ${
                canNavigateNext
                  ? 'text-gray-600 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-lighter'
                  : 'text-gray-400 dark:text-dark-text-secondary/50 cursor-not-allowed'
              }`}
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        </div>
      </div>

      <div className="h-[400px]">
        <Bar data={prepareChartData()} options={chartOptions} />
      </div>
    </div>
  );
} 